if (!customElements.get('l20-eight-video')) {
  class L20EightVideo extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.l20-eight-video__stage');
      this.videos = Array.from(this.querySelectorAll('.l20-eight-video__video'));
      this.scrollPerSegment = Number.parseFloat(this.dataset.scrollPerSegment) || 700;
      this.mobileScrollPerSegment =
        Number.parseFloat(this.dataset.mobileScrollPerSegment) || this.scrollPerSegment;
      this.scrollSegments = Math.max(1, Number.parseInt(this.dataset.scrollSegments, 10) || 3);
      this.enableSnap = this.dataset.snap === 'true';

      if (!this.stage || this.videos.length === 0) return;

      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'auto';
        video.pause();

        const revealVideo = () => video.classList.add('is-loaded');
        if (video.readyState >= 2) {
          revealVideo();
        } else {
          video.addEventListener('loadeddata', revealVideo, { once: true });
          video.addEventListener('canplay', revealVideo, { once: true });
          video.addEventListener('seeked', revealVideo, { once: true });
        }
      });

      const initiallyActiveVideo = this.getActiveVideo(window.innerWidth >= 769);
      this.pauseInactiveVideos(initiallyActiveVideo);
      if (initiallyActiveVideo && initiallyActiveVideo.readyState === 0) {
        initiallyActiveVideo.load();
      }

      if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
        this.enableFallback();
        return;
      }

      window.gsap.registerPlugin(window.ScrollTrigger);
      this.mediaContext = window.gsap.matchMedia();
      this.mediaContext.add(
        {
          isDesktop: '(min-width: 769px)',
          reduceMotion: '(prefers-reduced-motion: reduce)'
        },
        (context) => {
          const { isDesktop, reduceMotion } = context.conditions;
          const activeVideo = this.getActiveVideo(isDesktop);
          const scrollPerSegment = isDesktop ? this.scrollPerSegment : this.mobileScrollPerSegment;

          this.pauseInactiveVideos(activeVideo);
          if (isDesktop) {
            this.style.removeProperty('--l20-eight-mobile-video-ratio');
          } else {
            this.setMobileVideoRatio(activeVideo);
          }

          if (!activeVideo || reduceMotion) {
            this.enableFallback(activeVideo);
            return undefined;
          }

          const removeScrollVideo = isDesktop
            ? this.prepareScrollVideo(activeVideo, scrollPerSegment)
            : this.prepareMobileAutoplay(activeVideo);

          return () => {
            removeScrollVideo();
          };
        },
        this
      );
    }

    disconnectedCallback() {
      this.mediaContext?.revert();
      this.scrollTween?.kill();
      this.scrollTween = null;
      this.classList.remove('is-scroll-ready');
      this.videos?.forEach((video) => video.pause());
    }

    getActiveVideo(isDesktop) {
      const desktopVideo = this.querySelector('.l20-eight-video__video--desktop');
      const mobileVideo = this.querySelector('.l20-eight-video__video--mobile');
      return isDesktop ? desktopVideo || mobileVideo : mobileVideo || desktopVideo;
    }

    pauseInactiveVideos(activeVideo) {
      this.videos.forEach((video) => {
        video.pause();
        video.controls = false;
        video.preload = video === activeVideo ? 'auto' : 'none';
        if (video !== activeVideo && Number.isFinite(video.duration)) this.seekVideo(video, 0);
      });
    }

    enableFallback(activeVideo = this.getActiveVideo(window.innerWidth >= 769)) {
      this.classList.remove('is-scroll-ready');
      if (!activeVideo) return;
      activeVideo.controls = false;
      activeVideo.preload = 'metadata';
    }

    prepareMobileAutoplay(video) {
      let cancelled = false;
      let wasVisible = false;
      let removeMetadataListener = () => {};
      let observer = null;
      const observeTarget = this.querySelector('.l20-eight-video__media') || this;

      const playFromStart = async () => {
        if (cancelled || !video) return;

        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.autoplay = true;
        video.controls = false;
        video.preload = 'auto';
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        if (video.readyState === 0) video.load();
        this.seekVideo(video, 0);

        try {
          await video.play();
          if (!cancelled) {
            video.classList.add('is-loaded');
          }
        } catch (error) {
          // Muted autoplay can still be blocked on some mobile browsers; keep the poster visible.
        }
      };

      const initialize = () => {
        if (cancelled) return;

        this.setMobileVideoRatio(video);
        this.classList.add('is-scroll-ready');

        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(
            (entries) => {
              const entry = entries.find((item) => item.target === observeTarget);
              const isVisible = Boolean(
                entry && entry.isIntersecting && entry.intersectionRatio >= 0.25
              );

              if (isVisible && !wasVisible) {
                playFromStart();
              } else if (!isVisible && wasVisible) {
                video.pause();
              }

              wasVisible = isVisible;
            },
            {
              threshold: [0, 0.25, 0.5]
            }
          );
          observer.observe(observeTarget);
        } else {
          playFromStart();
        }
      };

      video.preload = 'auto';
      if (video.readyState >= 1) {
        initialize();
      } else {
        const handleMetadata = () => initialize();
        video.addEventListener('loadedmetadata', handleMetadata, { once: true });
        removeMetadataListener = () => video.removeEventListener('loadedmetadata', handleMetadata);
        video.load();
      }

      return () => {
        cancelled = true;
        removeMetadataListener();
        observer?.disconnect();
        video.pause();
        video.controls = false;
        this.classList.remove('is-scroll-ready');
      };
    }

    prepareMobileUnlock(video) {
      let unlocked = false;

      const removeListeners = () => {
        window.removeEventListener('touchstart', unlockVideo);
        window.removeEventListener('pointerdown', unlockVideo);
      };

      const unlockVideo = () => {
        if (unlocked) return;
        unlocked = true;
        removeListeners();

        const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const playPromise = video.play();

        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              video.pause();
              this.seekVideo(video, currentTime);
              video.classList.add('is-loaded');
            })
            .catch(() => {
              video.pause();
            });
        } else {
          video.pause();
          this.seekVideo(video, currentTime);
          video.classList.add('is-loaded');
        }
      };

      video.preload = 'auto';
      if (video.readyState === 0) video.load();
      window.addEventListener('touchstart', unlockVideo, { passive: true, once: true });
      window.addEventListener('pointerdown', unlockVideo, { passive: true, once: true });

      return removeListeners;
    }

    prepareInlineScrollVideo(video, scrollPerSegment) {
      let cancelled = false;
      let removeMetadataListener = () => {};
      const scrollDistance = () => scrollPerSegment * this.scrollSegments;

      const initialize = () => {
        if (cancelled || !Number.isFinite(video.duration) || video.duration <= 0) return;

        this.setMobileVideoRatio(video);
        video.controls = false;
        video.pause();
        this.seekVideo(video, 0);
        this.classList.add('is-scroll-ready');

        const playhead = { time: 0 };
        const seekEnd = Math.max(video.duration - 0.05, 0);

        this.scrollTween = window.gsap.to(playhead, {
          time: seekEnd,
          duration: 1,
          ease: 'none',
          onUpdate: () => {
            if (Math.abs(video.currentTime - playhead.time) > 0.016) {
              this.seekVideo(video, playhead.time);
            }
          },
          scrollTrigger: {
            id: `l20-eight-video-mobile-${this.id}`,
            trigger: this,
            start: 'top top',
            end: () => `+=${scrollDistance()}`,
            scrub: 0.25,
            snap: false,
            fastScrollEnd: false,
            invalidateOnRefresh: true
          }
        });

        if (window.scheduleScrollTriggerRefresh) {
          window.scheduleScrollTriggerRefresh();
        } else {
          window.ScrollTrigger.refresh();
        }
      };

      if (video.readyState >= 1) {
        initialize();
      } else {
        const handleMetadata = () => initialize();
        video.addEventListener('loadedmetadata', handleMetadata, { once: true });
        removeMetadataListener = () => video.removeEventListener('loadedmetadata', handleMetadata);
        video.load();
      }

      return () => {
        cancelled = true;
        removeMetadataListener();
        this.scrollTween?.kill();
        this.scrollTween = null;
        video.pause();
        video.controls = false;
        this.classList.remove('is-scroll-ready');
      };
    }

    prepareScrollVideo(video, scrollPerSegment) {
      let cancelled = false;
      let removeMetadataListener = () => {};
      const scrollDistance = () => scrollPerSegment * this.scrollSegments;

      const loadingTrigger = window.ScrollTrigger.create({
        id: `l20-eight-video-loading-${this.id}`,
        trigger: this,
        start: 'top top',
        end: () => `+=${scrollDistance()}`,
        pin: this.stage,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true
      });

      const initialize = () => {
        if (cancelled || !Number.isFinite(video.duration) || video.duration <= 0) return;

        loadingTrigger.kill(true);
        video.controls = false;
        video.pause();
        this.seekVideo(video, 0);
        this.classList.add('is-scroll-ready');

        const playhead = { time: 0 };
        const seekEnd = Math.max(video.duration - 0.05, 0);
        const snap = this.enableSnap
          ? {
              snapTo: this.getSnapPoints(),
              duration: { min: 0.12, max: 0.35 },
              delay: 0.08,
              ease: 'power1.inOut'
            }
          : false;

        this.scrollTween = window.gsap.to(playhead, {
          time: seekEnd,
          duration: 1,
          ease: 'none',
          onUpdate: () => {
            if (Math.abs(video.currentTime - playhead.time) > 0.016) {
              this.seekVideo(video, playhead.time);
            }
          },
          scrollTrigger: {
            id: `l20-eight-video-${this.id}`,
            trigger: this,
            start: 'top top',
            end: () => `+=${scrollDistance()}`,
            pin: this.stage,
            pinSpacing: true,
            scrub: 0.25,
            snap,
            anticipatePin: 1,
            fastScrollEnd: false,
            invalidateOnRefresh: true
          }
        });

        if (window.scheduleScrollTriggerRefresh) {
          window.scheduleScrollTriggerRefresh();
        } else {
          window.ScrollTrigger.refresh();
        }
      };

      if (video.readyState >= 1) {
        initialize();
      } else {
        const handleMetadata = () => initialize();
        video.addEventListener('loadedmetadata', handleMetadata, { once: true });
        removeMetadataListener = () => video.removeEventListener('loadedmetadata', handleMetadata);
        video.load();
      }

      return () => {
        cancelled = true;
        removeMetadataListener();
        loadingTrigger.kill(true);
        this.scrollTween?.kill();
        this.scrollTween = null;
        video.pause();
        video.controls = false;
        this.classList.remove('is-scroll-ready');
      };
    }

    getSnapPoints() {
      return Array.from({ length: this.scrollSegments + 1 }, (_, index) => index / this.scrollSegments);
    }

    setMobileVideoRatio(video) {
      if (!video || !Number.isFinite(video.videoWidth) || !Number.isFinite(video.videoHeight)) return;
      if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
      this.style.setProperty('--l20-eight-mobile-video-ratio', `${video.videoWidth} / ${video.videoHeight}`);
    }

    seekVideo(video, time) {
      try {
        video.currentTime = time;
      } catch (error) {
        // Some browsers briefly reject seeks while media data is still being attached.
      }
    }

  }

  customElements.define('l20-eight-video', L20EightVideo);
}
